import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import '../styles/product.css';
import { flagshipProducts } from '../data/products';
import SEO, { SITE_URL } from '../components/SEO';

const Product: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug: slugParam } = useParams<{ slug: string }>();

  // Active product is derived from the URL path (/product/:slug), matched
  // case-insensitively. Bare /product falls back to the first product.
  const slug = slugParam?.toLowerCase();
  const matchedIndex = flagshipProducts.findIndex((p) => p.slug === slug);
  const currentIndex = matchedIndex >= 0 ? matchedIndex : 0;
  const product = flagshipProducts[currentIndex];

  // Canonicalize legacy hash links (/product#supplymd) and non-lowercase
  // slugs (/product/SupplyMD) to the single indexable lowercase path.
  useEffect(() => {
    if (!slugParam && location.hash) {
      const hashSlug = location.hash.replace('#', '').toLowerCase();
      if (flagshipProducts.some((p) => p.slug === hashSlug)) {
        navigate(`/product/${hashSlug}`, { replace: true });
      }
    } else if (slugParam && slugParam !== slugParam.toLowerCase()) {
      navigate(`/product/${slugParam.toLowerCase()}`, { replace: true });
    }
  }, [slugParam, location.hash, navigate]);

  const goToDemo = () => navigate('/request-demo');
  const selectProduct = (s: string) => navigate(`/product/${s}`);

  const canonicalPath = slugParam ? `/product/${product.slug}` : '/product';

  const productListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: flagshipProducts.map((p, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'SoftwareApplication',
        name: p.name,
        description: p.description,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'iOS',
        url: `${SITE_URL}/product/${p.slug}`,
        ...(p.appStoreUrl ? { downloadUrl: p.appStoreUrl } : {}),
      },
    })),
  };

  return (
    <>
      <SEO
        title={`${product.name} — ${product.tagline}`}
        description={product.description.slice(0, 160)}
        path={canonicalPath}
        image={`${SITE_URL}${product.heroImage}`}
        structuredData={productListJsonLd}
      />
    <div className="product-page">
      <div className="product-page-header">
        <h1>Our products</h1>
        <p>
          Four operational systems, one platform. Each is sold and deployed on
          its own — and gets stronger when you run them together.
        </p>
        <div className="product-tabs">
          {flagshipProducts.map((p, i) => (
            <button
              key={p.slug}
              className={`product-tab ${i === currentIndex ? 'active' : ''}`}
              onClick={() => selectProduct(p.slug)}
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>

      <div className="product-panel" id={product.slug}>
        {/* Hero */}
        <div className="product-hero">
          <div className="product-hero-text">
            <h2>{product.name}</h2>
            <h3 className="product-tagline">{product.tagline}</h3>
            <p className="product-audience"><strong>Built for:</strong> {product.audience}</p>
            <p>{product.description}</p>
            <div className="hero-cta-row">
              {product.selfServe && product.appStoreUrl ? (
                <a
                  className="hero-cta primary"
                  href={product.appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get started free
                </a>
              ) : (
                <>
                  <button className="hero-cta primary" onClick={goToDemo}>
                    Request a demo
                  </button>
                  {product.appStoreUrl && (
                    <>
                      <span className="hero-cta-or">or</span>
                      <a
                        className="app-store-badge-link"
                        href={product.appStoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Try ${product.name} yourself — download on the App Store`}
                      >
                        <img src="/images/icons/badge-appstore.png" alt="Download on the App Store" />
                      </a>
                    </>
                  )}
                </>
              )}
            </div>
            {product.selfServe && (
              <p className="hero-cta-secondary-link">
                Need {product.name} for multiple locations?{' '}
                <button type="button" className="link-button" onClick={goToDemo}>
                  Talk to sales
                </button>
              </p>
            )}
          </div>
          <div className="product-hero-screenshot">
            <img src={product.heroImage} alt={`${product.name} preview`} />
          </div>
        </div>

        {/* Getting started (self-serve products only) */}
        {product.gettingStarted && (
          <div className="product-getting-started">
            <h3 className="product-section-title">Getting started</h3>
            <div className="getting-started-steps">
              {product.gettingStarted.map((step, idx) => (
                <div key={idx} className="getting-started-step">
                  <span className="getting-started-step-number">{idx + 1}</span>
                  <div>
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Workflow */}
        <div className="product-workflow">
          <h3 className="product-section-title">How it works</h3>
          <div className="workflow-steps">
            {product.workflow.map((step, idx) => (
              <div key={idx} className="workflow-step">
                <div className="workflow-step-screenshot">
                  <img
                    src={step.image ?? product.heroImage}
                    alt={`${product.name} step ${idx + 1}`}
                  />
                </div>
                <div className="workflow-step-text">
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Capabilities + Outcomes */}
        <div className="product-grid-two-col">
          <div className="product-info">
            <h4>Capabilities</h4>
            <ul className="features-list">
              {product.features.map((feature, idx) => <li key={idx}>{feature}</li>)}
            </ul>
          </div>
          <div className="product-info">
            <h4>Outcomes</h4>
            <ul className="benefits-list">
              {product.benefits.map((benefit, idx) => <li key={idx}>{benefit}</li>)}
            </ul>
          </div>
        </div>

        <div className="product-footer-cta">
          {product.selfServe && product.appStoreUrl ? (
            <>
              <a
                className="hero-cta primary"
                href={product.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Get started with {product.name}
              </a>
              <p className="hero-cta-secondary-link">
                Need it for multiple locations?{' '}
                <button type="button" className="link-button" onClick={goToDemo}>
                  Talk to sales
                </button>
              </p>
            </>
          ) : (
            <button className="hero-cta primary" onClick={goToDemo}>
              Talk to sales about {product.name}
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default Product;
